import { useState } from 'react'
import Card from '../../../components/common/Card/Card'
import Button from '../../../components/common/Button/Button'
import Badge from '../../../components/common/Badge/Badge'
import { formatFileSize } from '../../../services/cloudinaryService'
import { formatUploadDate } from '../../../services/salarySlipsService'

/**
 * SlipCard Component
 * Displays a single salary slip with download/delete actions
 *
 * @param {object} slip - Salary slip data
 * @param {boolean} isAdmin - Whether current user is admin
 * @param {function} onDownload - Download handler
 * @param {function} onDelete - Delete handler
 */
const SlipCard = ({ slip, isAdmin, onDownload, onDelete }) => {
  const [deleting, setDeleting] = useState(false)

  const handleDelete = async () => {
    const confirmMessage = `Are you sure you want to delete the salary slip for ${slip.employeeName} - ${slip.month} ${slip.year}?`

    if (!window.confirm(confirmMessage)) return

    setDeleting(true)
    try {
      await onDelete(slip.id, slip.cloudinaryPublicId)
    } catch (error) {
      console.error('Delete error:', error)
    } finally {
      setDeleting(false)
    }
  }

  const handleDownload = () => {
    const fileName = `${slip.employeeName}_Salary_Slip_${slip.month}_${slip.year}.pdf`
    onDownload(slip.cloudinaryUrl, fileName)
  }

  return (
    <Card
      interactive={!deleting}
      style={{
        opacity: deleting ? 0.6 : 1,
        pointerEvents: deleting ? 'none' : 'auto'
      }}
    >
      {/* Header with Month/Year */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '1rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid var(--psyber-card-border)'
      }}>
        <i
          className="fas fa-file-pdf"
          style={{
            fontSize: '2rem',
            color: 'var(--psyber-error)'
          }}
        ></i>
        <div style={{ flex: 1 }}>
          <h3 style={{
            margin: 0,
            fontSize: 'var(--psyber-font-size-lg)',
            fontWeight: 600,
            color: 'var(--psyber-text)'
          }}>
            {slip.month} {slip.year}
          </h3>
        </div>
      </div>

      {/* Employee Info (Admin View Only) */}
      {isAdmin && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem',
          background: 'var(--psyber-info-bg)',
          borderRadius: 'var(--psyber-radius-md)',
          border: '1px solid var(--psyber-info)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.5rem'
          }}>
            <i className="fas fa-user" style={{ color: 'var(--psyber-info)' }}></i>
            <strong style={{ fontSize: 'var(--psyber-font-size-base)', color: 'var(--psyber-text)' }}>
              {slip.employeeName}
            </strong>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: 'var(--psyber-font-size-sm)',
            color: 'var(--psyber-text-secondary)'
          }}>
            <Badge variant="info" style={{ fontSize: 'var(--psyber-font-size-xs)' }}>
              {slip.employeeId}
            </Badge>
            <span style={{ fontSize: 'var(--psyber-font-size-xs)' }}>
              {slip.employeeEmail}
            </span>
          </div>
        </div>
      )}

      {/* File Info */}
      <div style={{
        display: 'grid',
        gap: '0.5rem',
        marginBottom: '1rem'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: 'var(--psyber-font-size-sm)',
          color: 'var(--psyber-text-secondary)'
        }}>
          <i className="fas fa-file" style={{ width: '1rem', color: 'var(--psyber-primary)' }}></i>
          <span>Size: {formatFileSize(slip.fileSize)}</span>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: 'var(--psyber-font-size-sm)',
          color: 'var(--psyber-text-secondary)'
        }}>
          <i className="fas fa-calendar" style={{ width: '1rem', color: 'var(--psyber-primary)' }}></i>
          <span>Uploaded: {formatUploadDate(slip.uploadedAt)}</span>
        </div>

        {isAdmin && slip.uploadedByName && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: 'var(--psyber-font-size-sm)',
            color: 'var(--psyber-text-secondary)'
          }}>
            <i className="fas fa-user-check" style={{ width: '1rem', color: 'var(--psyber-primary)' }}></i>
            <span>By: {slip.uploadedByName}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        marginTop: '1.5rem',
        paddingTop: '1rem',
        borderTop: '1px solid var(--psyber-card-border)'
      }}>
        <Button
          variant="primary"
          icon="fas fa-download"
          onClick={handleDownload}
          style={{ flex: 1 }}
        >
          Download
        </Button>

        {isAdmin && (
          <Button
            variant="ghost"
            icon="fas fa-trash"
            onClick={handleDelete}
            loading={deleting}
            disabled={deleting}
            style={{
              color: 'var(--psyber-error)',
              borderColor: 'var(--psyber-error)',
              flex: 1
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        )}
      </div>
    </Card>
  )
}

export default SlipCard
